import { Button, Grid, Group, Paper, SimpleGrid, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconArrowRight,
  IconCards,
  IconMeeple,
  IconPlayerPause,
  IconSchool,
  IconX,
} from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEMO_SCENARIO_LABELS, type SessionStatus } from '@creativitas/shared';
import { SessionStatusBadge } from '@/components/domain/SessionStatusBadge';
import { PageHeader } from '@/components/ui/PageHeader';
import { CardGridSkeleton, EmptyState, ErrorState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { pluralize } from '@/lib/plural';
import {
  AvailabilitySummary,
  ManualGrantsTile,
  OwnedGamesTile,
  SubscriptionTile,
  TrialTile,
} from '@/features/access/AccessStateCards';
import { useAvailability, useSession } from '@/features/auth/useSession';
import { fetchSpreadSessions, SPREAD_SESSIONS_KEY } from '@/features/spreads/api';
import { cancelWaitingSession, fetchGameSessions, GAME_SESSIONS_KEY } from '@/features/games/api';

/**
 * Dashboard (§16): greeting, commercial state, owned games, quick links.
 *
 * Deliberately free of invented analytics — it shows facts the access engine
 * actually knows.
 */
export function DashboardPage() {
  const { user, access, isLoading, isError, refetch } = useSession();
  const availability = useAvailability(Boolean(user));
  /*
    "Continue your work" is a live list: a facilitator pauses a session and comes
    straight back here. The global 30s staleTime would show them a cached list
    without the session they just paused, so these two always refetch on mount.
  */
  const liveList = { enabled: Boolean(user), staleTime: 0, refetchOnMount: 'always' } as const;

  const spreadSessions = useQuery({
    queryKey: SPREAD_SESSIONS_KEY,
    queryFn: fetchSpreadSessions,
    ...liveList,
  });
  const gameSessions = useQuery({
    queryKey: GAME_SESSIONS_KEY,
    queryFn: fetchGameSessions,
    ...liveList,
  });

  if (isLoading) return <CardGridSkeleton count={3} />;
  if (isError || !user || !access) {
    return <ErrorState onRetry={() => void refetch()} />;
  }

  const firstName = user.displayName.split(' ')[0] ?? user.displayName;

  return (
    <>
      <PageHeader
        title={`Здравствуйте, ${firstName}`}
        description="Ваши МАК-сессии, игры и материалы. Здесь же видно, что открыто по вашему доступу."
      />

      <Stack gap="xl">
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          <TrialTile trial={access.trial} subscriptionActive={access.subscription.active} />
          <SubscriptionTile subscription={access.subscription} />
          <OwnedGamesTile games={access.ownedGames} />
          <ManualGrantsTile grants={access.manualGrants} />
        </SimpleGrid>

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Stack gap="lg">
              <Stack gap="sm">
                <Title order={3}>Продолжить работу</Title>
                <RecentSessions
                  spreads={spreadSessions}
                  games={gameSessions}
                />
              </Stack>

              <Stack gap="sm">
                <Title order={3}>Быстрый переход</Title>
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                  <QuickLink
                    to="/spreads"
                    icon={<IconCards size={20} stroke={1.6} />}
                    title="МАК-сессии"
                    hint="Готовые сессии и свободный стол"
                  />
                  <QuickLink
                    to="/games"
                    icon={<IconMeeple size={20} stroke={1.6} />}
                    title="Игры"
                    hint="Создать сессию и позвать участников"
                  />
                  <QuickLink
                    to="/training"
                    icon={<IconSchool size={20} stroke={1.6} />}
                    title="Обучение"
                    hint="Материалы для ведущего"
                  />
                </SimpleGrid>
              </Stack>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 5 }}>
            {availability.isLoading ? (
              <Paper withBorder p="md" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
                <Text size="sm" c="var(--text-muted)">
                  Считаем доступные материалы...
                </Text>
              </Paper>
            ) : availability.isError ? (
              <ErrorState
                title="Не удалось посчитать доступ"
                onRetry={() => void availability.refetch()}
              />
            ) : (
              <AvailabilitySummary items={availability.data?.items ?? []} />
            )}
          </Grid.Col>
        </Grid>
      </Stack>
    </>
  );
}

/**
 * «Продолжить работу» — один список из двух источников.
 *
 * Раскладам и играм раньше выделялись отдельные секции, из-за чего пустой
 * список раскладов писал «активных сессий пока нет» прямо над четырьмя
 * идущими партиями. Пусто здесь — это когда пусто и там, и там.
 *
 * Карточки различаются знаком: тот же, что у раздела в навигации, в подложке
 * своего цвета. Без него игра и расклад — две одинаковые белые плитки.
 */
type SessionQuery<T> = {
  isLoading: boolean;
  isError: boolean;
  data?: { items: T[] };
  refetch: () => unknown;
};

/**
 * Сколько сессий видно сразу. Остальные — по «Все незавершённые».
 *
 * Прежде лимит был единственным способом их увидеть: пятая по счёту
 * незавершённая сессия пропадала совсем. На тесте 16 сентября так «потерялись»
 * «Шахматы» — Дарья открыла ещё несколько раскладов, и вернуться в идущую
 * групповую сессию стало неоткуда.
 */
const RECENT_LIMIT = 4;

function RecentSessions({
  spreads,
  games,
}: {
  spreads: SessionQuery<Awaited<ReturnType<typeof fetchSpreadSessions>>['items'][number]>;
  games: SessionQuery<Awaited<ReturnType<typeof fetchGameSessions>>['items'][number]>;
}) {
  const [showAll, setShowAll] = useState(false);
  const queryClient = useQueryClient();
  const cancel = useMutation({
    mutationFn: cancelWaitingSession,
    onSuccess: () => {
      notifications.show({ color: 'teal', message: 'Сессия отменена' });
      void queryClient.invalidateQueries({ queryKey: SPREAD_SESSIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_KEY });
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });
  /*
    Сессия в «Ожидании» не начиналась: участники не подключились или их ещё не
    звали. Её можно отменить — иначе она висит здесь, а убрать её нечем.
  */
  const askCancel = (id: string, title: string) =>
    modals.openConfirmModal({
      title: 'Отменить сессию?',
      children: (
        <Text size="sm" c="var(--text-muted)">
          «{title}» ещё не началась. Сессия будет удалена, ссылка-приглашение перестанет
          работать. Если кто-то уже зашёл по ссылке, он увидит, что сессии больше нет.
        </Text>
      ),
      labels: { confirm: 'Отменить сессию', cancel: 'Оставить' },
      confirmProps: { color: 'red' },
      onConfirm: () => cancel.mutate(id),
    });
  const live = <T extends { status: string }>(query: SessionQuery<T>): T[] =>
    (query.data?.items ?? []).filter((item) => item.status !== 'completed');

  if (spreads.isLoading || games.isLoading) return <CardGridSkeleton count={2} />;
  if (spreads.isError && games.isError) {
    return (
      <ErrorState
        title="Не удалось загрузить сессии"
        onRetry={() => {
          void spreads.refetch();
          void games.refetch();
        }}
      />
    );
  }

  /* Одна лента из двух источников, сверху — где что-то происходило последним. */
  const items = [
    ...live(spreads).map((item) => ({
      id: item.id,
      lastActivityAt: item.lastActivityAt,
      card: (
        <SessionCard
          key={item.id}
          to={`/spreads/${item.spreadSlug}/session/${item.id}`}
          icon={<IconCards size={17} stroke={1.7} />}
          tint="var(--surface-accent-soft)"
          mark="var(--accent)"
          title={item.spreadTitle}
          status={item.status}
          onCancel={item.status === 'waiting' ? () => askCancel(item.id, item.spreadTitle) : undefined}
          meta={`${item.isSolo ? 'Индивидуально' : 'Групповая сессия'} · ${pluralize(item.cardCount, 'карта', 'карты', 'карт')}`}
        />
      ),
    })),
    ...live(games).map((item) => ({
      id: item.id,
      lastActivityAt: item.lastActivityAt,
      card: (
        <SessionCard
          key={item.id}
          to={`/games/${item.gameSlug}/session/${item.id}`}
          icon={<IconMeeple size={17} stroke={1.7} />}
          tint="var(--surface-gold-soft)"
          mark="var(--gold-deep)"
          title={item.gameTitle}
          status={item.status}
          onCancel={item.status === 'waiting' ? () => askCancel(item.id, item.gameTitle) : undefined}
          meta={item.demoScenario
            ? `Демоверсия · ${DEMO_SCENARIO_LABELS[item.demoScenario].toLowerCase()}`
            : `Игровая сессия · ${pluralize(item.participantCount, 'участник', 'участника', 'участников')}`}
        />
      ),
    })),
  ].sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<IconPlayerPause size={26} stroke={1.5} />}
        title="Активных сессий пока нет"
        description="Начните индивидуальную сессию или соберите групповую. Состояние сохранится после обновления страницы."
      />
    );
  }

  const visible = showAll ? items : items.slice(0, RECENT_LIMIT);

  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" data-testid="recent-sessions">
        {visible.map((item) => item.card)}
      </SimpleGrid>
      {items.length > RECENT_LIMIT ? (
        <Group>
          <Button
            variant="subtle"
            size="sm"
            onClick={() => setShowAll((current) => !current)}
            data-testid="recent-sessions-toggle"
          >
            {showAll ? 'Свернуть' : `Все незавершённые (${items.length})`}
          </Button>
        </Group>
      ) : null}
    </Stack>
  );
}

function SessionCard({
  to,
  icon,
  tint,
  mark,
  title,
  status,
  meta,
  onCancel,
}: {
  to: string;
  icon: ReactNode;
  tint: string;
  mark: string;
  title: string;
  status: SessionStatus;
  meta: string;
  /** Только у сессии в «Ожидании»: кнопка стоит рядом со ссылкой, а не внутри неё. */
  onCancel?: () => void;
}) {
  return (
    <Paper withBorder p="md" style={{ borderColor: 'var(--border-subtle)' }} data-testid="session-card">
      <Link to={to} style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <ThemeIcon size={30} radius="md" variant="default" style={{ background: tint, color: mark, borderColor: 'transparent' }}>
                {icon}
              </ThemeIcon>
              <Text fw={600} c="var(--text-primary)" style={{ minWidth: 0 }}>
                {title}
              </Text>
            </Group>
            <SessionStatusBadge status={status} />
          </Group>
          <Text size="sm" c="var(--text-muted)">{meta}</Text>
        </Stack>
      </Link>
      {onCancel ? (
        <Group justify="flex-end" mt="xs">
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            leftSection={<IconX size={13} />}
            onClick={onCancel}
            data-testid="session-cancel"
          >
            Отменить сессию
          </Button>
        </Group>
      ) : null}
    </Paper>
  );
}

function QuickLink({
  to,
  icon,
  title,
  hint,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  hint: string;
}) {
  return (
    <Paper
      component={Link}
      to={to}
      withBorder
      p="md"
      radius="lg"
      style={{ borderColor: 'var(--border-subtle)', textDecoration: 'none', display: 'block' }}
    >
      <Stack gap={6}>
        <Group gap="xs" c="var(--accent)">
          {icon}
          <Text fw={600} c="var(--text-primary)">
            {title}
          </Text>
        </Group>
        <Text size="sm" c="var(--text-muted)">
          {hint}
        </Text>
        <Group gap={4} c="var(--accent)">
          <Text size="xs" fw={600}>
            Открыть
          </Text>
          <IconArrowRight size={13} />
        </Group>
      </Stack>
    </Paper>
  );
}
